<template>
  <div class="add-follow-record-container">
    <van-form @submit="submitForm">
      <van-cell-group>
        <DictSelect
          v-model="formData.followType"
          name="followType"
          label="跟进方式"
          placeholder="请选择跟进方式"
          :columns="followMethodList"
          required
          @confirm="(data) => { formData.followTypeText = data.label }"
        />

        <DictSelect
          v-model="formData.followStage"
          name="followStage"
          label="跟进阶段"
          placeholder="请选择跟进阶段"
          :columns="followPhaseList"
          required
          @confirm="(data) => { formData.followStageText = data.label }"
        />

        <van-field v-model="formData.followContent" name="followContent" label="跟进内容" placeholder="添加跟进内容(1000字以内)"
          type="textarea" rows="5" maxlength="1000" show-word-limit required />

        <van-field v-model="formData.nextFollowTimeText" name="nextFollowTime" label="下次跟进时间" placeholder="请选择下次跟进时间"
          is-link readonly clickable @click="showNextFollowTimePicker = true" />
      </van-cell-group>

      <!-- 附件上传 -->
      <div class="file-upload-section">
        <div class="file-upload-label">附件</div>
        <van-uploader v-model="fileList" :max-count="4" :before-read="beforeRead" :after-read="afterRead"
          :max-size="5 * 1024 * 1024" accept="image/*" multiple @delete="deleteFile">
          <div class="file-upload-box">
            <div class="file-upload-icon">+</div>
            <div class="file-upload-text">点击上传文件</div>
            <div class="file-upload-hint">支持png/jpg/jpeg格式，单文件不超过5MB</div>
          </div>
        </van-uploader>
      </div>

      <div class="form-actions">
        <van-button @click="goBack" type="default" block>取消</van-button>
        <van-button type="primary" block native-type="submit" :loading="loading">保存</van-button>
      </div>
    </van-form>

    <!-- 下次跟进时间选择器 -->
    <van-popup v-model:show="showNextFollowTimePicker" position="bottom">
      <van-date-picker v-model="selectedDateTime" type="date" @confirm="onNextFollowTimeConfirm"
        @cancel="showNextFollowTimePicker = false" />
    </van-popup>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router';
import { showSuccessToast, showFailToast } from 'vant';
import { addConsultationRecord } from '@/api/consultationBook';
import { uploadFile } from '@/api/oss';
import { useDict } from '@/utils/dict';
import DictSelect from '@/components/DictSelect.vue';

const route = useRoute()
const router = useRouter()
const { follow_up_method: followMethodList, follow_up_phase: followPhaseList, loadDicts } = useDict(
  'follow_up_method',
  'follow_up_phase'
)

const consultationBookId = ref('')
const loading = ref(false)
const selectedDateTime = ref(getCurrentDateTimeArray())
const formData = reactive({
  followType: '',
  followTypeText: '',
  followStage: '',
  followStageText: '',
  followContent: '',
  nextFollowTime: '',
  nextFollowTimeText: '',
  consultationBookId: '',
  annex: ''
})
const showNextFollowTimePicker = ref(false)
const fileList = ref([])
const selectedFiles = ref([])

onMounted(async () => {
  consultationBookId.value = route.query.consultationBookId
  if (!consultationBookId.value) {
    showFailToast('缺少必要参数')
    router.push('/consultationBook')
    return
  }
  formData.consultationBookId = consultationBookId.value
  try {
    await loadDicts()
  } catch (error) {
    showFailToast('加载字典数据失败')
  }
})

function getCurrentDateTimeArray() {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return [year.toString(), month, day]
}

function beforeRead(files) {
  const uploadFiles = Array.isArray(files) ? files : [files]
  for (const file of uploadFiles) {
    if (!file || !file.type) {
      showFailToast('文件格式异常')
      return false
    }
    if (!file.type.startsWith('image/')) {
      showFailToast('只能上传图片文件')
      return false
    }
    if (file.size > 5 * 1024 * 1024) {
      showFailToast('图片大小不能超过5MB')
      return false
    }
  }
  return true
}

async function afterRead(files) {
  const uploadFiles = Array.isArray(files) ? files : [files]
  for (const file of uploadFiles) {
    try {
      file.status = 'uploading'
      file.message = '上传中...'
      const uploadFormData = new FormData()
      uploadFormData.append('file', file.file)
      const response = await uploadFile(uploadFormData)
      file.status = 'success'
      file.message = '上传成功'
      file.url = response.url
      file.fileName = response.fileName
      file.ossId = response.ossId
      selectedFiles.value.push(response.ossId)
      formData.annex = selectedFiles.value.join(',')
    } catch (error) {
      file.status = 'failed'
      file.message = '上传失败'
      showFailToast('上传失败，请重试')
      console.error('文件上传失败:', error)
    }
  }
}

function deleteFile(file, index) {
  if (typeof index === 'number' && index >= 0 && index < fileList.value.length) {
    fileList.value.splice(index, 1)
    selectedFiles.value.splice(index, 1)
    formData.annex = selectedFiles.value.join(',')
  }
}

function onNextFollowTimeConfirm() {
  formData.nextFollowTimeText = `${selectedDateTime.value[0]}-${selectedDateTime.value[1]}-${selectedDateTime.value[2]}`
  const [year, month, day] = selectedDateTime.value
  formData.nextFollowTime = `${year}-${month}-${day} 00:00:00`
  showNextFollowTimePicker.value = false
}

async function submitForm() {
  if (!formData.followType) {
    showFailToast('请选择跟进方式')
    return
  }
  if (!formData.followStage) {
    showFailToast('请选择跟进阶段')
    return
  }
  if (!formData.followContent) {
    showFailToast('请填写跟进内容')
    return
  }

  loading.value = true
  const requestParams = {
    ...formData,
    followTypeText: undefined,
    followStageText: undefined,
    nextFollowTimeText: undefined
  }

  addConsultationRecord(requestParams)
    .then(() => {
      window.consultationBookNeedRefresh = true
      showSuccessToast('添加成功')
      goBack()
    })
    .catch(() => {
      showFailToast('添加失败，请重试')
    }).finally(() => {
      loading.value = false
    })
}

function goBack() {
  router.back()
}
</script>

<style scoped>
.add-follow-record-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

/* 附件上传区域 */
.file-upload-section {
  padding: 16px;
  background-color: #fff;
  margin-top: -1px;
}

.file-upload-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
  display: block;
}

.file-upload-container {
  position: relative;
}

.file-upload-box {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-upload-box:active {
  border-color: #1989fa;
  background-color: #f0f8ff;
}

.file-upload-icon {
  font-size: 32px;
  color: #d9d9d9;
  margin-bottom: 8px;
}

.file-upload-text {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.file-upload-hint {
  font-size: 12px;
  color: #999;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.uploaded-files {
  margin-top: 12px;
}

.uploaded-file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 6px;
  margin-bottom: 8px;
}

.file-info {
  flex: 1;
  overflow: hidden;
}

.file-name {
  display: block;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.file-remove {
  font-size: 18px;
  color: #999;
  cursor: pointer;
  margin-left: 12px;
  transition: color 0.3s ease;
}

.file-remove:active {
  color: #ff4d4f;
}

/* 表单按钮区域 */
.form-actions {
  padding: 16px;
  display: flex;
  gap: 12px;
  background-color: #fff;
  margin-top: 20px;
}



/* 响应式设计 */
@media (max-width: 414px) {
  .file-upload-box {
    padding: 16px;
  }

  .file-upload-icon {
    font-size: 24px;
  }

  .form-actions {
    padding: 12px;
  }
}
</style>
